import { parseTsToMillis } from './matchEventsFeed';

/**
 * Canonical Sniper event ordering — the match that most recently produced a
 * goal / VAR / reversal signal sits at the TOP of the group's card list, and
 * everything with no signal keeps the backend's original order.
 *
 * Two sources feed the same comparator:
 *   - the DURABLE per-event stamps on `GET /sniper/groups`
 *     (`lastGoalSignalAt` / `lastVarAt` / `lastReversalAt`), which is what makes
 *     the order survive a page refresh; and
 *   - the LIVE per-fixture stamps reduced from the `/match-events` feed, which
 *     re-order instantly instead of waiting for the 15s poll.
 * The two are merged by taking the NEWEST stamp seen from either side, so the
 * slow-path poll can only ever confirm a live signal, never roll it back.
 */

/** The three signal kinds that float a match to the top. */
export type SniperSignalKind = 'goal' | 'var' | 'reversal';

/** Latest timestamp per signal kind (ISO 8601). Absent/null = never seen. */
export interface SniperSignalTimes {
  /** Last goal-confidence reading — at ANY confidence, not a confirmed score. */
  lastGoalSignalAt?: string | null;
  /** Last VAR review. */
  lastVarAt?: string | null;
  /** Last goal retraction / reversal. */
  lastReversalAt?: string | null;
}

/** Map field for a signal kind — the ONE place the kind→field mapping lives. */
export const SIGNAL_FIELD: Record<SniperSignalKind, keyof SniperSignalTimes> = {
  goal: 'lastGoalSignalAt',
  var: 'lastVarAt',
  reversal: 'lastReversalAt',
};

/**
 * Recency of the newest signal in `times`, in epoch millis, or `0` when there
 * is none (an unparseable stamp counts as none). `0` — not `-Infinity` — so a
 * never-signalled event and a garbage-stamped one compare EQUAL and the
 * comparator returns 0 for them, preserving the incoming order.
 */
export function signalRecencyMs(
  ...times: Array<SniperSignalTimes | null | undefined>
): number {
  let best = 0;
  for (const t of times) {
    if (!t) continue;
    for (const field of Object.values(SIGNAL_FIELD)) {
      const ms = parseTsToMillis(t[field]);
      if (Number.isFinite(ms) && ms > best) best = ms;
    }
  }
  return best;
}

/** The minimum an event needs to be ordered: an id and (maybe) its stamps. */
export interface OrderableSniperEvent extends SniperSignalTimes {
  fixtureId: number;
}

/**
 * Order a group's events most-recent-signal FIRST.
 *
 * Contract:
 *  - An event's recency is the NEWEST of its own six candidate stamps: the
 *    three persisted on the event and the three from `live` for its
 *    `fixtureId`. Any goal / VAR / reversal signal counts, at any confidence.
 *  - Events with NO signal (and events whose newest signals are equal) compare
 *    0, and `Array.prototype.sort` is stable, so they keep their incoming
 *    (backend) order. The sort never invents an ordering it wasn't given.
 *  - Returns a NEW array; the input is never mutated.
 */
export function orderSniperEvents<T extends OrderableSniperEvent>(
  events: T[] | undefined,
  live?: ReadonlyMap<number, SniperSignalTimes>,
): T[] {
  const list = [...(events ?? [])];
  if (list.length < 2) return list;
  const recency = new Map<number, number>();
  for (const e of list) {
    recency.set(e.fixtureId, signalRecencyMs(e, live?.get(e.fixtureId)));
  }
  return list.sort(
    (a, b) => (recency.get(b.fixtureId) ?? 0) - (recency.get(a.fixtureId) ?? 0),
  );
}
